import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  ClientLink,
  Currency,
  ExportDropdown,
  InternalClientTooltip,
  Percent,
  ProjectLink,
  RouteLink,
  Widget,
} from '~/components';
import ClientFiltersBar from '~/components/filters/ClientFiltersBar';
import ClientFiltersGroup from '~/components/filters/ClientFiltersGroup';
import CurrencyFilter from '~/components/filters/CurrencyFilter';
import ExpenseFiltersBar from '~/components/filters/ExpenseFiltersBar';
import ExpenseFiltersGroup from '~/components/filters/ExpenseFiltersGroup';
import FilterButton from '~/components/filters/FilterButton';
import MemberFiltersBar from '~/components/filters/MemberFiltersBar';
import MemberFiltersGroup from '~/components/filters/MemberFiltersGroup';
import ProjectFiltersBar from '~/components/filters/ProjectFiltersBar';
import ProjectFiltersGroup from '~/components/filters/ProjectFiltersGroup';
import ReportPeriodFilter from '~/components/filters/ReportPeriodFilter';
import { useApi, useConfirmation, useWorkspace } from '~/contexts';
import {
  useAuth,
  useDocumentTitle,
  useExpenseFilters,
  useExpenseItemsReportURL,
  useFeatures,
  useSearchParams,
  useSearchParamsConfig,
} from '~/hooks';
import useClientFilters from '~/hooks/useClientFilters';
import useMemberFilters from '~/hooks/useMemberFilters';
import useProjectFilters from '~/hooks/useProjectFilters';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import { QuerySort } from '~/utils';
import { sumBy } from '~/utils/math';
import mimeTypes from '~/utils/mimeTypes';
import ExportDialogAsync from '../components/ExportDialogAsync';
import Meter from '../components/Meter';
import { Metric, Metrics } from '../components/Metric';
import Report from '../components/Report';
import Table from '../components/table';
import useReportsSearchParamsConfig from '../hooks/useReportsSearchParamsConfig';
import ReportActions from '../ReportActions';

const ProjectInfo = styled.div`
  line-height: 1;
  margin-left: 1rem;

  small {
    font-size: 0.75rem;
    color: ${colors.grey40};
    display: flex;
    padding-top: 0.25rem;
  }
`;

const Byline = styled.small`
  display: block;
`;

function ExpenseSummaryByClientAndProject({ report }) {
  useDocumentTitle(report.name);
  const { workspace } = useWorkspace();
  const api = useApi();
  const features = useFeatures();

  const [query, setQuery] = useState({
    report: null,
    status: 'loading',
    loading: {
      summary: false,
      table: false,
    },
  });

  const clientFilters = useClientFilters();
  const projectFilters = useProjectFilters();
  const memberFilters = useMemberFilters();
  const expenseFilters = useExpenseFilters();

  const [params, setParams] = useState({
    period: null,
    currency: workspace.currency,
    sort: new QuerySort('name', 'asc'),
    ...clientFilters.filters,
    ...projectFilters.filters,
    ...memberFilters.filters,
    ...expenseFilters.filters,
  });

  // Init and sync URL search params
  const searchParamsConfig = useSearchParamsConfig();
  const reportsSearchParamsConfig = useReportsSearchParamsConfig();
  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        period: reportsSearchParamsConfig.period,
        currency: searchParamsConfig.currency,
        sort: { default: new QuerySort('client.name', 'asc'), ...searchParamsConfig.sort },
        ...clientFilters.searchParamsConfig,
        ...projectFilters.searchParamsConfig,
        ...memberFilters.searchParamsConfig,
        ...expenseFilters.searchParamsConfig,
      }),
      [searchParamsConfig, reportsSearchParamsConfig, clientFilters, projectFilters, memberFilters, expenseFilters],
    ),

    onChange: (params) => setParams((state) => ({ ...state, ...params })),
  });

  // Map the values to perform the API query
  const urlSearchParams = useMemo(
    () => ({
      start: params.period?.start ?? undefined,
      end: params.period?.end ?? undefined,
      currency: params.currency ?? undefined,
      sort: params.sort,
      ...clientFilters.mapUrlSearchParams(params),
      ...projectFilters.mapUrlSearchParams(params),
      ...memberFilters.mapUrlSearchParams(params),
      ...expenseFilters.mapUrlSearchParams(params),
    }),
    [params, clientFilters, projectFilters, memberFilters, expenseFilters],
  );

  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((params) => {
      setParams((state) => ({ ...state, ...params }));
      setSearchParamsStatus('ready');
    });
  }, [searchParams, searchParamsStatus]);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).reports(report.id).data(urlSearchParams);
    setQuery((state) => ({
      ...state,
      data,
      status: 'ready',
      loading: {
        summary: false,
        table: false,
      },
    }));
  }, [api, workspace.id, urlSearchParams, report.id]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);
  const handleApplyFilters = (values) => {
    if (values !== params) setQuery((state) => ({ ...state, status: 'filtering' }));

    setParams({ ...params, ...values });
    searchParams.set(_.omit(values, 'sort'));
    hideFilters();
  };

  const handleSort = ({ column, sort }) => {
    setQuery((state) => ({ ...state, loading: { ...state.loading, table: true } }));
    const direction = column === sort.column && sort.direction === 'asc' ? 'desc' : 'asc';
    const querySort = new QuerySort(column, direction);
    setParams({ ...params, sort: querySort });
    searchParams.set({ sort: querySort });
  };

  const handleCurrencyChange = (currency) => {
    setQuery((state) => ({ ...state, loading: { ...state.loading, summary: true, table: true } }));
    setParams({ ...params, page: 0, currency });
    searchParams.set({ currency });
  };

  const confirmation = useConfirmation();

  const handleExport = (mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialogAsync
        onLoad={api.www
          .workspaces(workspace.id)
          .reports(report.id)
          .data(urlSearchParams, { headers: { accept: mimeType } })}
        onClose={resolve}
      />
    ));
  };

  return (
    <Report>
      <Report.Header>
        <Report.Info report={report} />

        <Report.Actions>
          <ReportActions onSave={() => searchParams.set(params)} />
          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <FilterButton isOutline onClick={showFilters} />
        </Report.Actions>
      </Report.Header>

      <Report.FiltersBar>
        <ReportPeriodFilter value={params.period} onChange={({ value }) => handleApplyFilters({ period: value })} />

        {features.multicurrency && (
          <CurrencyFilter value={params.currency} onChange={({ target: { value } }) => handleCurrencyChange(value)} />
        )}

        <ClientFiltersBar filters={params} onChange={handleApplyFilters} />

        <ProjectFiltersBar filters={params} onChange={handleApplyFilters} />

        <MemberFiltersBar filters={params} onChange={handleApplyFilters} />

        <ExpenseFiltersBar filters={params} onChange={handleApplyFilters} />
      </Report.FiltersBar>

      {(() => {
        switch (query.status) {
          case 'loading':
          case 'filtering':
            return <PageLoader />;

          default:
            return (
              <>
                <Data query={query} params={params} onSort={handleSort} onCurrencyChange={handleCurrencyChange} />
                <Filters values={params} isOpen={filtersVisible} onApply={handleApplyFilters} onClose={hideFilters} />
              </>
            );
        }
      })()}
    </Report>
  );
}

function Data({ query, params, onSort }) {
  const report = query.data;

  const groups = useMemo(() => {
    return _.groupBy(report.records, (record) => `${record.client.id}`);
  }, [report.records]);

  const expenseDetail = useExpenseItemsReportURL({ params });

  return (
    <>
      <Report.Summary fade={query.loading.summary}>
        <Widget>
          <Meter>
            <Meter.Value
              label="Billable Cost"
              value={report.totals.billable}
              color={colors.success}
              tooltip={
                <>
                  <Currency value={report.totals.billable} currency={report.currency} /> (
                  <Percent value={report.totals.billable / report.totals.cost} />)
                </>
              }
            />
            <Meter.Value
              label="Non-Billable Cost"
              value={report.totals.nonBillable}
              color={colors.danger}
              tooltip={
                <>
                  <Currency value={report.totals.nonBillable} currency={report.currency} /> (
                  <Percent value={report.totals.nonBillable / report.totals.cost} />)
                </>
              }
            />
          </Meter>

          <Metrics>
            <Metric>
              <RouteLink to={expenseDetail({ expenseBillableTypeId: 'billable' })}>
                <Metric.Value>
                  <Currency value={report.totals.billable} currency={report.currency} />
                </Metric.Value>
                <Metric.Label>Billable Cost</Metric.Label>
              </RouteLink>
            </Metric>
            <Metric>
              {/* TODO: add link to expense detail once it supports filtering by "project expenses only" */}
              <Metric.Value>
                <Currency value={report.totals.nonBillable} currency={report.currency} />
              </Metric.Value>
              <Metric.Label>Non-Billable Cost</Metric.Label>
            </Metric>
            <Metric>
              {/* TODO: add link to expense detail once it supports filtering by "project expenses only" */}
              <Metric.Value>
                <Currency value={report.totals.cost} currency={report.currency} />
              </Metric.Value>
              <Metric.Label>Total Cost</Metric.Label>
            </Metric>
          </Metrics>
        </Widget>
      </Report.Summary>

      <Report.Table>
        <Table>
          <Table.Header>
            <Table.Column sticky minWidth="16rem" name="client.name" onSort={onSort} sort={params.sort}>
              Client
            </Table.Column>
            <Table.Column minWidth="16rem">Project Type</Table.Column>
            <Table.Column width="10rem" align="right">
              Billable Cost
            </Table.Column>
            <Table.Column width="10rem" align="right">
              Non-Billable Cost
            </Table.Column>
            <Table.Column width="10rem" align="right">
              Total Cost
            </Table.Column>
            <Table.Column width="10rem" align="right">
              Markup
            </Table.Column>
            <Table.Column width="10rem" align="right">
              Total Revenue
            </Table.Column>
          </Table.Header>

          <Table.Body fade={query.loading.table}>
            {_.map(groups, (projects, clientId) => {
              const client = projects[0].client;
              const clientBillable = sumBy(projects, 'convertedBillable');
              const clientNonBillable = sumBy(projects, 'convertedNonBillable');
              const cost = sumBy(projects, 'convertedCost');
              const markup = sumBy(projects, 'convertedMarkup');
              const revenue = sumBy(projects, 'convertedRevenue');

              return (
                <React.Fragment key={clientId}>
                  <Table.GroupRow top="2.95rem">
                    <Table.Cell>
                      <strong>
                        <ClientLink client={client} />
                      </strong>
                      {client.isInternal && <InternalClientTooltip />}
                    </Table.Cell>
                    <Table.Cell />
                    <Table.Cell>
                      <RouteLink to={expenseDetail({ expenseBillableTypeId: 'billable', clientId: client.id })}>
                        <strong>
                          <Currency value={clientBillable} currency={report.currency} />
                        </strong>
                      </RouteLink>
                    </Table.Cell>
                    <Table.Cell>
                      <RouteLink to={expenseDetail({ expenseBillableTypeId: 'non_billable', clientId: client.id })}>
                        <strong>
                          <Currency value={clientNonBillable} currency={report.currency} />
                        </strong>
                      </RouteLink>
                    </Table.Cell>
                    <Table.Cell>
                      <RouteLink to={expenseDetail({ clientId: client.id })}>
                        <strong>
                          <Currency value={cost} currency={report.currency} />
                        </strong>
                      </RouteLink>
                    </Table.Cell>
                    <Table.Cell>
                      <strong>
                        <Currency value={markup} currency={report.currency} />
                      </strong>
                    </Table.Cell>
                    <Table.Cell>
                      <strong>
                        <Currency value={revenue} currency={report.currency} />
                      </strong>
                    </Table.Cell>
                  </Table.GroupRow>

                  {_.map(projects, (project) => {
                    return (
                      <Table.Row key={project.id}>
                        <Table.Cell>
                          <ProjectInfo>
                            <p>
                              <ProjectLink project={project} />
                            </p>
                          </ProjectInfo>
                        </Table.Cell>
                        <Table.Cell>{project.projectType?.name}</Table.Cell>
                        <Table.Cell>
                          <p>
                            <RouteLink to={expenseDetail({ expenseBillableTypeId: 'billable', projectId: project.id })}>
                              <Currency value={project.convertedBillable} currency={report.currency} />
                            </RouteLink>
                            {project.currency !== report.currency && (
                              <Byline>
                                <Currency value={project.billable} currency={project.currency} />
                              </Byline>
                            )}
                          </p>
                        </Table.Cell>
                        <Table.Cell>
                          <p>
                            <RouteLink
                              to={expenseDetail({ expenseBillableTypeId: 'non_billable', projectId: project.id })}>
                              <Currency value={project.convertedNonBillable} currency={report.currency} />
                            </RouteLink>
                            {project.currency !== report.currency && (
                              <Byline>
                                <Currency value={project.nonBillable} currency={project.currency} />
                              </Byline>
                            )}
                          </p>
                        </Table.Cell>
                        <Table.Cell>
                          <p>
                            <RouteLink to={expenseDetail({ projectId: project.id })}>
                              <Currency value={project.convertedCost} currency={report.currency} />
                            </RouteLink>
                            {project.currency !== report.currency && (
                              <Byline>
                                <Currency value={project.cost} currency={project.currency} />
                              </Byline>
                            )}
                          </p>
                        </Table.Cell>
                        <Table.Cell>
                          <p>
                            <RouteLink to={expenseDetail({ projectId: project.id })}>
                              <Currency value={project.convertedMarkup} currency={report.currency} />
                            </RouteLink>
                            {project.currency !== report.currency && (
                              <Byline>
                                <Currency value={project.markup} currency={project.currency} />
                              </Byline>
                            )}
                          </p>
                        </Table.Cell>
                        <Table.Cell>
                          <p>
                            <RouteLink to={expenseDetail({ projectId: project.id })}>
                              <Currency value={project.convertedRevenue} currency={report.currency} />
                            </RouteLink>
                            {project.currency !== report.currency && (
                              <Byline>
                                <Currency value={project.revenue} currency={project.currency} />
                              </Byline>
                            )}
                          </p>
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </React.Fragment>
              );
            })}
            <Table.Row style={{ fontWeight: weights.bold }}>
              <Table.Cell>Total</Table.Cell>
              <Table.Cell />
              <Table.Cell>
                <RouteLink to={expenseDetail({ expenseBillableTypeId: 'billable' })}>
                  <Currency value={report.totals.billable} currency={report.currency} />
                </RouteLink>
              </Table.Cell>
              <Table.Cell>
                {/* TODO: add link to expense detail once it supports filtering by "project expenses only" */}
                <Currency value={report.totals.nonBillable} currency={report.currency} />
              </Table.Cell>
              <Table.Cell>
                {/* TODO: add link to expense detail once it supports filtering by "project expenses only" */}
                <Currency value={report.totals.cost} currency={report.currency} />
              </Table.Cell>
              <Table.Cell>
                <Currency value={report.totals.markup} currency={report.currency} />
              </Table.Cell>
              <Table.Cell>
                <Currency value={report.totals.revenue} currency={report.currency} />
              </Table.Cell>
            </Table.Row>
          </Table.Body>

          <Table.Status
            total={_.size(groups)}
            label="Client"
            isLoading={query.status !== 'ready'}
            partial={report.partialResults}
          />
        </Table>
      </Report.Table>
    </>
  );
}

function Filters({ values, isOpen, onClose, onApply }) {
  const auth = useAuth();
  const [filters, setFilters] = useState(values);

  const handleApply = () => {
    onApply(filters);
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <Report.FiltersDrawer isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      <ReportPeriodFilter
        value={filters.period}
        onChange={({ target: { value } }) => handleFilter({ period: value })}
      />

      <ClientFiltersGroup filters={filters} onChange={handleFilter} />

      <ProjectFiltersGroup filters={filters} onChange={handleFilter} />

      {auth.expenses.view && <MemberFiltersGroup filters={filters} onChange={handleFilter} />}

      <ExpenseFiltersGroup filters={filters} onChange={handleFilter} />
    </Report.FiltersDrawer>
  );
}

export default ExpenseSummaryByClientAndProject;
